import React from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import { FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import messages from './messages';
import style from './style.module.less';
import FormattedCurrency from '../../../../../components/formatted-currency';
import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../../../../store/global-selectors';
import { useGetCouponAdjustedTotalCost, currentBillingPeriodFilter } from '../../hooks/use-get-coupon-adjusted-monthly-plan-cost';
import { DiscountBadge } from '../../components/new-lineup-plan-grid/plan-details/discount-badge/discount-badge';
import { useCouponForPlanCode, useSubscriptionCoupons } from '../../hooks/use-coupon';

/**
 * @typedef {object} LineItem
 * @property {string} lineItemId
 * @property {object} price
 * @property {number} price.value
 * @property {string} price.code
 */

/**
 * @param {object} props
 * @param {Array<LineItem>} [props.lineItems]
 * @returns {React.ReactElement}
 */
export const PlanTotalCost = ({ lineItems = emptyArray }) => {
  const isOnAnnualBilling = useSelector(
    (state) => Global.selectSellerSubscriptionData(state)?.currentBillingPlan?.isOnAnnualBilling ?? false,
  );
  const currentPlanCode = useSelector((state) => Global.selectSellerSubscriptionData(state)?.currentBillingPlan?.code);

  const totalCostValue = lineItems.reduce((acc, { price }) => acc + price?.value, 0);
  const totalCostCode = lineItems.find((item) => item?.price?.code)?.price?.code;

  const coupons = useSubscriptionCoupons();
  const { currentBillingPeriodCost } = useGetCouponAdjustedTotalCost(coupons, totalCostValue, totalCostCode);
  const applicableCoupon = useCouponForPlanCode(currentPlanCode);
  const hasCoupons = Boolean(applicableCoupon);

  // Extract appliedCoupon first, derive hasAppliedCoupons from it
  // Use applicableCoupon (already filtered by plan) then check billing period — avoids showing discount for non-applicable plans
  const appliedCoupon = applicableCoupon && currentBillingPeriodFilter(applicableCoupon) ? applicableCoupon : undefined;
  const hasAppliedCoupons = Boolean(appliedCoupon);
  const hasPriceChanged = hasAppliedCoupons && currentBillingPeriodCost.value !== totalCostValue;
  // If we actually ever use flat-dollar coupons, this is the place to handle that
  const discountPercent = appliedCoupon?.discountPercent;

  return (
    <div className={style['plan-line-items']}>
      {lineItems.length > 0 && (
        <div className={style['plan-total']}>
          <Text
            className={style['total-label']}
            textType={TextType.H3}
            value={isOnAnnualBilling ? messages.yearlyTotal : messages.monthlyTotal}
          />
          <div className={style['total-cost']}>
            {hasPriceChanged ? (
              <FlexRow className={style['discount-row']} data-testid='discount-row' gap={Gap.Space8}>
                <span className={style['original-price-strikethrough']}>
                  <FormattedCurrency currencyCode={totalCostCode} value={totalCostValue} />
                </span>
                <span className={style['discounted-price']}>
                  <FormattedCurrency currencyCode={currentBillingPeriodCost.code} value={currentBillingPeriodCost.value} />
                </span>
                {discountPercent && <DiscountBadge discountPercent={discountPercent} variant='coupon' />}
              </FlexRow>
            ) : (
              <FormattedCurrency currencyCode={totalCostCode} value={totalCostValue} />
            )}
          </div>
        </div>
      )}
      {hasCoupons && (
        <div className={classnames(style.subtext, style.asterisk)} data-testid='coupon-testId'>
          <FormattedMessage {...messages.appliedCouponExplainerGeneric} />
        </div>
      )}
    </div>
  );
};
